// Epic 5: Approval workflow hooks
// Stories: 5.1, 5.2, 5.3, 5.5, 5.6, 5.7, 5.8
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Claim, FlaggedBy } from './useClaims';

// ============================================================
// Types
// ============================================================

export interface ClaimantInfo {
  id: string;
  name: string;
  email: string;
  department: string | null;
}

export interface ExpenseSummary {
  mileageCount: number;
  pettyCashCount: number;
  mileageTotal: number;
  pettyCashTotal: number;
}

export interface QueueClaimFlag {
  entryId: string;
  reason: string;
  flaggedAt: string | null;
  flaggedBy: FlaggedBy | null;
}

export interface QueueClaim extends Claim {
  user: ClaimantInfo;
  expenseSummary: ExpenseSummary;
  flags?: QueueClaimFlag[];
}

export interface QueueResponse {
  claims: QueueClaim[];
  meta: { pagination: { page: number; limit: number; total: number; totalPages: number } };
}

export interface FinanceQueueResponse extends QueueResponse {
  dashboard: {
    totalClaims: number;
    totalAmountPending: number;
  };
}

// ============================================================
// Query keys
// ============================================================

export const approvalKeys = {
  all: ['approval'] as const,
  adminQueue: () => [...approvalKeys.all, 'queue'] as const,
  financeQueue: () => [...approvalKeys.all, 'finance-queue'] as const,
  hrQueue: () => [...approvalKeys.all, 'hr-queue'] as const,
};

// ============================================================
// Story 5.1 — Admin Queue
// ============================================================

export function useAdminQueue(params?: { page?: number; sortBy?: string; status?: string; month?: string }) {
  const page = params?.page ?? 1;
  const sortBy = params?.sortBy ?? 'submittedAt';
  
  return useQuery<QueueResponse>({
    queryKey: [...approvalKeys.adminQueue(), params],
    queryFn: () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        sortBy,
      });
      if (params?.status) searchParams.append('status', params.status);
      if (params?.month) searchParams.append('month', params.month);
      
      return api.get(`/approval/queue?${searchParams.toString()}`);
    },
  });
}

// ============================================================
// Story 5.2 — Admin Approve
// ============================================================

export function useApproveClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, string>({
    mutationFn: (claimId) => api.post(`/approval/${claimId}/approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.adminQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
    },
  });
}

// ============================================================
// Story 5.3 — Admin / Multi-Stage Reject
// ============================================================

export function useRejectClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, { claimId: string; reason: string }>({
    mutationFn: ({ claimId, reason }) => api.post(`/approval/${claimId}/reject`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.adminQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}

// ============================================================
// Story 5.5 — Finance Queue
// ============================================================

export function useFinanceQueue(params?: { page?: number; hardcopy?: string; month?: string }) {
  const page = params?.page ?? 1;
  const hardcopy = params?.hardcopy;
  const month = params?.month;
  return useQuery<FinanceQueueResponse>({
    queryKey: [...approvalKeys.financeQueue(), { page, hardcopy, month }],
    queryFn: () => {
      const q = new URLSearchParams({ page: page.toString() });
      if (hardcopy && hardcopy !== 'ALL') q.append('hardcopy', hardcopy);
      if (month) q.append('month', month);
      return api.get(`/approval/finance-queue?${q.toString()}`);
    },
  });
}

// ============================================================
// Issue #15 (B2) — HR Queue
// ============================================================

export type HrQueueResponse = FinanceQueueResponse;

export function useHrQueue(params?: { page?: number }) {
  const page = params?.page ?? 1;
  return useQuery<HrQueueResponse>({
    queryKey: [...approvalKeys.hrQueue(), { page }],
    queryFn: () => api.get(`/approval/hr-queue?page=${page}`),
  });
}

// ============================================================
// Story 5.6 & B1 — Finance Lead Approve
// ============================================================

export function useFinalApproveClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, string>({
    mutationFn: (claimId) => api.post(`/approval/${claimId}/final-approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}

// ============================================================
// Story 5.7 — Finance Reject
// ============================================================

export function useFinanceRejectClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, { claimId: string; reason: string }>({
    mutationFn: ({ claimId, reason }) => api.post(`/approval/${claimId}/finance-reject`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
    },
  });
}

// ============================================================
// Issue #14 (B1) — HR Final Approve
// ============================================================

export function useHrApproveClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, string>({
    mutationFn: (claimId) => api.post(`/approval/${claimId}/hr-approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}

// ============================================================
// Issue #14 (B1) — HR Reject
// ============================================================

export function useHrRejectClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, { claimId: string; reason: string }>({
    mutationFn: ({ claimId, reason }) => api.post(`/approval/${claimId}/hr-reject`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}

// ============================================================
// Story 5.8 — Resubmit Rejected Claim
// ============================================================

export function useResubmitClaim(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error>({
    mutationFn: () => api.post(`/approval/${claimId}/resubmit`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
  });
}

// ============================================================
// Issue #16 (B3) — Verify / Flag Expense Entry
// ============================================================

export interface VerifyEntryPayload {
  entryId: string;
  entryType: 'mileage' | 'petty-cash';
  action: 'verify' | 'unverify' | 'flag' | 'clear-flag';
  reason?: string;
}

export function useVerifyEntry(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ entry: any; claimStatus?: string }, Error, VerifyEntryPayload>({
    mutationFn: (payload) => api.post(`/approval/${claimId}/verify-entry`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['claims', claimId, 'expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['claims', claimId] });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.adminQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}

// ============================================================
// Issue #16 (B3) & Issue #13 (A7) — Mark Hardcopy Received
// ============================================================

export function useMarkHardcopyReceived(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, { received?: boolean } | void>({
    mutationFn: (payload) => api.post(`/approval/${claimId}/hardcopy-received`, payload ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['claims', claimId] });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.adminQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.financeQueue() });
      void queryClient.invalidateQueries({ queryKey: approvalKeys.hrQueue() });
    },
  });
}
